import debounce from "lodash/debounce";
import isObject from "lodash/isObject";
import { useCallback, useMemo } from "react";
import { Control } from "react-hook-form";

import { ValidationError } from "@/lib/hook/useValidation";

import {
  ImmigramFormCheckbox,
  ImmigramFormDate,
  ImmigramFormElementConfigCheckbox,
  ImmigramFormElementConfigDate,
  ImmigramFormElementConfigHeading,
  ImmigramFormElementConfigHidden,
  ImmigramFormElementConfigParagraph,
  ImmigramFormElementConfigSelect,
  ImmigramFormElementConfigText,
  ImmigramFormElementConfigTextarea,
  ImmigramFormElementConfigToggle,
  ImmigramFormHeading,
  ImmigramFormHidden,
  ImmigramFormParagraph,
  ImmigramFormSelect,
  ImmigramFormText,
  ImmigramFormTextarea,
  ImmigramFormToggle,
} from "./elements";
import {
  ImmigramFormElementConfig,
  ImmigramFormElementType,
  ImmigramFormInputElementConfig,
} from "./elements/common";

interface ImmigramFormElementProps {
  commentThreadId?: string;
  elementConfig: ImmigramFormElementConfig;
  control: Control;
  validationErrors?: ValidationError | null;
  onChange: (
    elementConfig: ImmigramFormInputElementConfig,
    value: string
  ) => void;
  disabled: boolean;
  withComments?: boolean;
}

function ImmigramFormElement({
  commentThreadId,
  elementConfig,
  control,
  validationErrors,
  onChange,
  disabled,
  withComments,
}: ImmigramFormElementProps) {
  const handleElementChange = useCallback(
    (field: string, value: any) => {
      onChange(elementConfig as ImmigramFormInputElementConfig, String(value));
    },
    [onChange, elementConfig]
  );

  const handleElementChangeDebounced = useMemo(
    () => debounce(handleElementChange, 500),
    [handleElementChange]
  );

  const itemProps = {
    control,
    onChange: handleElementChangeDebounced,
    disabled,
    commentThreadId,
    validationErrors,
    withComments,
  };

  if (
    !isObject(elementConfig) ||
    elementConfig.type === ImmigramFormElementType.Paragraph
  ) {
    return (
      <ImmigramFormParagraph
        {...{
          ...itemProps,
          elementConfig: elementConfig as ImmigramFormElementConfigParagraph,
        }}
      />
    );
  }

  if (elementConfig.type === ImmigramFormElementType.Heading) {
    return (
      <ImmigramFormHeading
        {...{
          ...itemProps,
          elementConfig: elementConfig as ImmigramFormElementConfigHeading,
        }}
      />
    );
  }

  if (elementConfig.type === ImmigramFormElementType.Hidden) {
    return (
      <ImmigramFormHidden
        {...{
          ...itemProps,
          elementConfig: elementConfig as ImmigramFormElementConfigHidden,
        }}
      />
    );
  }

  if (elementConfig.type === ImmigramFormElementType.Textarea) {
    return (
      <ImmigramFormTextarea
        {...{
          ...itemProps,
          elementConfig: elementConfig as ImmigramFormElementConfigTextarea,
        }}
      />
    );
  }

  if (elementConfig.type === ImmigramFormElementType.Checkbox) {
    return (
      <ImmigramFormCheckbox
        {...{
          ...itemProps,
          elementConfig: elementConfig as ImmigramFormElementConfigCheckbox,
        }}
      />
    );
  }

  if (elementConfig.type === ImmigramFormElementType.Select) {
    return (
      <ImmigramFormSelect
        {...{
          ...itemProps,
          elementConfig: elementConfig as ImmigramFormElementConfigSelect,
        }}
      />
    );
  }

  if (elementConfig.type === ImmigramFormElementType.Text) {
    return (
      <ImmigramFormText
        {...{
          ...itemProps,
          elementConfig: elementConfig as ImmigramFormElementConfigText,
        }}
      />
    );
  }

  if (elementConfig.type === ImmigramFormElementType.Date) {
    return (
      <ImmigramFormDate
        {...{
          ...itemProps,
          elementConfig: elementConfig as ImmigramFormElementConfigDate,
        }}
      />
    );
  }

  if (elementConfig.type === ImmigramFormElementType.Toggle) {
    return (
      <ImmigramFormToggle
        {...{
          ...itemProps,
          elementConfig: elementConfig as ImmigramFormElementConfigToggle,
        }}
      />
    );
  }

  if ((elementConfig as any).type) {
    throw new Error(
      `Unknown form element type: ${(elementConfig as any).type}`
    );
  }

  return null;
}

export { ImmigramFormElement };
